"use client";

import { useMemo, useState } from "react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ReferenceArea,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { HistoricalSeries, YearValue } from "@/lib/data/types";
import { formatPercent, formatRatio } from "@/lib/format";
import { computeNiceYAxisTicks } from "@/lib/price-chart-utils";
import { useDragZoom } from "@/lib/use-drag-zoom";
import { cn } from "@/lib/utils";

const MIN_SPAN_YEARS = 1;

export interface PeerKpiSeriesInput {
  key: string;
  /** Voller Firmenname statt Ticker (Erik 2026-07-11, Feedback-Runde 8 Punkt 5). */
  label: string;
  color: string;
  historical: HistoricalSeries;
}

type KpiValueFormat = "percent" | "ratio";

interface KpiOption {
  key: keyof HistoricalSeries;
  label: string;
  format: KpiValueFormat;
}

/** Nur Kennzahlen mit echter Jahres-Zeitreihe (HistoricalSeries) - im Gegensatz zur
 * bestehenden Momentaufnahme-Vergleichstabelle unten (die auch Fundamentals-only-Werte
 * wie Marktkapitalisierung/EV-EBITDA/ROA/D-E zeigt, fuer die keine Historie vorliegt). */
const KPI_OPTIONS: KpiOption[] = [
  { key: "netMargin", label: "Nettomarge", format: "percent" },
  { key: "grossMargin", label: "Bruttomarge", format: "percent" },
  { key: "roic", label: "ROIC", format: "percent" },
  { key: "roe", label: "ROE", format: "percent" },
  { key: "pe", label: "KGV (P/E)", format: "ratio" },
  { key: "pb", label: "KBV (P/B)", format: "ratio" },
  { key: "priceToSales", label: "KUV (P/S)", format: "ratio" },
  { key: "dividendYield", label: "Dividendenrendite", format: "percent" },
];

function formatValue(format: KpiValueFormat, v: number): string {
  return format === "percent" ? formatPercent(v) : formatRatio(v);
}

/** Eindeutiger Datenkey je (Aktie x Kennzahl)-Kombination - noetig, sobald mehrere
 * Kennzahlen gleichzeitig im selben Chart landen (siehe GroupChart unten), sonst wuerden
 * z.B. "netMargin" und "roic" derselben Aktie denselben Recharts-dataKey teilen. */
function seriesDataKey(seriesKey: string, optionKey: string): string {
  return `${seriesKey}__${optionKey}`;
}

/** Linienmuster je Kennzahl INNERHALB eines Charts (Farbe bleibt je Aktie konstant ueber
 * alle Charts hinweg, siehe PeerKpiSeriesInput.color) - erste ausgewaehlte Kennzahl eines
 * Formats bleibt durchgezogen, weitere bekommen ein zunehmend distinktes Strichmuster. */
const KPI_DASH_PATTERNS: (string | undefined)[] = [undefined, "6 3", "2 3", "8 3 2 3"];

/**
 * Ein Chart je Kennzahl-FORMAT-Gruppe (Prozent/Ratio), mit einer Linie je (Aktie x
 * Kennzahl)-Kombination (backlog.md [Punkt 71]: "zusätzliche KPI-Auswahl... als weitere
 * Linie ins selbe Diagramm einblenden, statt jedes Mal einen komplett neuen Chart zu
 * öffnen") - Kennzahlen mit UNTERSCHIEDLICHEM Format (z.B. Nettomarge % + KGV Ratio)
 * bleiben bewusst in separaten Charts, da eine gemeinsame Y-Achse dort nicht sinnvoll
 * interpretierbar waere; innerhalb desselben Formats teilen sich mehrere Kennzahlen jetzt
 * ein Chart. Gleiches Klick+Ziehen-Zoom-Muster wie die uebrigen Charts im Projekt (siehe
 * components/charts/multi-kpi-line-chart.tsx), hier aber eigenstaendig statt
 * wiederverwendet, da die Serien-Dimension eine andere ist (mehrere AKTIEN x KENNZAHLEN
 * statt mehrere KENNZAHLEN je EINER Aktie). */
function GroupChart({ options, series }: { options: KpiOption[]; series: PeerKpiSeriesInput[] }) {
  const format = options[0].format;
  const multiKpi = options.length > 1;

  const chartData = useMemo(() => {
    const byYear = new Map<number, { year: number } & Record<string, number | undefined>>();
    for (const option of options) {
      for (const s of series) {
        const points = s.historical[option.key] as YearValue[];
        for (const point of points) {
          const entry = byYear.get(point.year) ?? { year: point.year };
          entry[seriesDataKey(s.key, option.key)] = point.value;
          byYear.set(point.year, entry);
        }
      }
    }
    return [...byYear.values()].sort((a, b) => a.year - b.year);
  }, [series, options]);

  // Mausrad-/Tastatur-Zoom (neu, Siebzehnte autonome Charge) ueber denselben gemeinsamen
  // Hook wie die uebrigen Zeitreihen-Charts - siehe lib/use-drag-zoom.ts. Vereinheitlicht
  // dabei nebenbei das vorher hier fehlende rAF-Throttling beim Ziehen (siehe
  // historical-line-chart.tsx fuer die Begruendung).
  const {
    zoomRange,
    resetZoom,
    refAreaLeft,
    refAreaRight,
    chartWrapperRef,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onKeyDown,
    keyboardA11yProps,
  } = useDragZoom({
    fullMin: chartData[0]?.year,
    fullMax: chartData[chartData.length - 1]?.year,
    minSpan: MIN_SPAN_YEARS,
  });

  const visibleData = useMemo(() => {
    if (!zoomRange) return chartData;
    const [lo, hi] = zoomRange;
    return chartData.filter((d) => d.year >= lo && d.year <= hi);
  }, [chartData, zoomRange]);

  const yearDomain = useMemo((): [number, number] | undefined => {
    const years = visibleData.map((d) => d.year);
    return years.length > 0 ? [Math.min(...years), Math.max(...years)] : undefined;
  }, [visibleData]);

  // Ersetzt Rechartsʼ Auto-Tick-Heuristik, die den obersten Tick teils nicht auf den
  // gleichen Rhythmus wie die uebrigen Ticks rundete (backlog.md -> Runde 5 Punkt 19).
  const yAxisTicks = useMemo(
    () =>
      computeNiceYAxisTicks(
        visibleData.flatMap((row) =>
          options.flatMap((option) =>
            series.map((s) => row[seriesDataKey(s.key, option.key)]).filter((v): v is number => typeof v === "number"),
          ),
        ),
        false,
      ),
    [visibleData, series, options],
  );

  if (chartData.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Für „{options.map((o) => o.label).join("/")}“ liegt bei keiner der verglichenen Aktien eine Historie vor.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-ink-secondary">
          {options.map((o) => o.label).join(" & ")} im Zeitverlauf
        </span>
        {zoomRange && (
          <button
            type="button"
            onClick={resetZoom}
            className="text-xs font-medium text-secondary underline underline-offset-2 hover:text-ink"
          >
            Zoom zurücksetzen
          </button>
        )}
      </div>
      <div
        ref={chartWrapperRef}
        className="h-56 w-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        onKeyDown={onKeyDown}
        {...keyboardA11yProps}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={visibleData}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
          >
            <CartesianGrid stroke="#16265C" strokeOpacity={0.12} strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="year"
              type="number"
              domain={yearDomain ?? ["auto", "auto"]}
              allowDecimals={false}
              tickFormatter={(v: number) => String(Math.round(v))}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              axisLine={{ stroke: "#ECEAE3" }}
              tickLine={false}
            />
            <YAxis
              domain={[yAxisTicks[0], yAxisTicks[yAxisTicks.length - 1]]}
              ticks={yAxisTicks}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              tickFormatter={(v: number) => formatValue(format, v)}
              width={64}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              formatter={(value, name) => [formatValue(format, Number(value)), name]}
              labelFormatter={(label) => String(label)}
              cursor={{ stroke: "#16265C", strokeOpacity: 0.25, strokeWidth: 1, strokeDasharray: "3 3" }}
              contentStyle={{
                borderRadius: 4,
                borderColor: "#ECEAE3",
                fontSize: 13,
                boxShadow: "0 4px 16px rgba(22,38,92,0.08)",
              }}
            />
            <Legend formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>} />
            {options.flatMap((option, optionIndex) =>
              series.map((s) => (
                <Line
                  key={seriesDataKey(s.key, option.key)}
                  type="linear"
                  dataKey={seriesDataKey(s.key, option.key)}
                  name={multiKpi ? `${s.label} – ${option.label}` : s.label}
                  stroke={s.color}
                  strokeWidth={2}
                  strokeDasharray={KPI_DASH_PATTERNS[optionIndex % KPI_DASH_PATTERNS.length]}
                  dot={false}
                  activeDot={{ r: 4, fill: s.color, stroke: "#FAF9F5", strokeWidth: 2 }}
                  isAnimationActive={false}
                  connectNulls
                />
              )),
            )}
            {refAreaLeft !== null && refAreaRight !== null && (
              <ReferenceArea x1={refAreaLeft} x2={refAreaRight} fill="#16265C" fillOpacity={0.12} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/** Gruppiert die ausgewaehlten Kennzahlen nach Format (Prozent/Ratio, siehe GroupChart
 * oben) - Reihenfolge folgt KPI_OPTIONS, damit die Chart-Reihenfolge stabil bleibt,
 * unabhaengig davon, in welcher Reihenfolge im Dropdown angeklickt wurde. */
function groupByFormat(options: KpiOption[]): KpiOption[][] {
  const groups = new Map<KpiValueFormat, KpiOption[]>();
  for (const opt of options) {
    const list = groups.get(opt.format) ?? [];
    list.push(opt);
    groups.set(opt.format, list);
  }
  return [...groups.values()];
}

/** Auffälliges Mehrfachauswahl-Dropdown (backlog.md [Punkt 72]) - ersetzt die vorherige
 * Checkbox-Reihe UND ersetzt fuer die 8 Kennzahlen mit echter Historie die entsprechenden
 * Zeilen der separaten "Aktuelle Kennzahlen"-Tabelle darunter (siehe peer-compare.tsx, dort
 * auf die verbleibenden 4 wirklich historienlosen Kennzahlen verschlankt). Popover+Checkbox
 * statt eines nativen <select multiple> - konsistent mit dem uebrigen Projekt (kein
 * dropdown-menu.tsx vorhanden, Popover ist bereits das etablierte Muster fuer
 * Auswahl-Overlays, siehe peer-compare.tsx). */
function KpiMultiSelect({
  options,
  selected,
  onToggle,
}: {
  options: KpiOption[];
  selected: Set<string>;
  onToggle: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedLabels = options.filter((o) => selected.has(o.key)).map((o) => o.label);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium shadow-sm transition-colors",
            selectedLabels.length > 0
              ? "border-gold bg-gold/10 text-ink hover:bg-gold/15"
              : "border-hairline bg-card text-ink-secondary hover:border-gold",
          )}
        >
          <SlidersHorizontal className="h-4 w-4 text-primary" aria-hidden="true" />
          Kennzahlen wählen
          {selectedLabels.length > 0 && (
            <span className="rounded-full bg-primary px-1.5 py-0.5 font-tabular text-[0.65rem] text-primary-foreground">
              {selectedLabels.length}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64">
        <p className="px-1 pb-1 text-xs font-medium text-ink-secondary">Mehrere gleichzeitig wählbar</p>
        {options.map((opt) => (
          <label
            key={opt.key}
            className="flex cursor-pointer items-center gap-2.5 rounded-md px-1.5 py-1.5 text-sm text-ink hover:bg-muted"
          >
            <Checkbox checked={selected.has(opt.key)} onCheckedChange={() => onToggle(opt.key)} />
            {opt.label}
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Kennzahlen-Zeitverlaufs-Vergleich fuer das Aktien-Duell (Erik 2026-07-11,
 * Feedback-Runde 8 Punkt 6: "nicht nur die KPIs untereinander stehen ... sondern dass
 * man auswählen kann, welche KPIs man im Zeitverlauf der jeweiligen Aktien vergleichen
 * möchte, man kann auch mehrere gleichzeitig auswählen"). Mehrfachauswahl-Dropdown
 * (backlog.md [Punkt 72], vorher eine Checkbox-Reihe) - ausgewaehlte Kennzahlen werden
 * nach Format gruppiert (backlog.md [Punkt 71]): mehrere Kennzahlen DESSELBEN Formats
 * (z.B. Nettomarge + ROIC, beide Prozent) teilen sich jetzt EIN Chart statt je ein
 * eigenes zu oeffnen, unterschiedliche Formate (Prozent vs. Ratio) bleiben weiterhin
 * getrennt, da eine gemeinsame Y-Achse dort nicht sinnvoll interpretierbar waere.
 */
export function PeerKpiComparison({ series }: { series: PeerKpiSeriesInput[] }) {
  const availableOptions = useMemo(
    () => KPI_OPTIONS.filter((opt) => series.some((s) => (s.historical[opt.key] as YearValue[]).length > 0)),
    [series],
  );
  const [selected, setSelected] = useState<Set<string>>(() => new Set(["netMargin"]));

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  if (availableOptions.length === 0) return null;

  const selectedOptions = availableOptions.filter((opt) => selected.has(opt.key));
  const groups = groupByFormat(selectedOptions);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-ink-secondary">Kennzahlen im Zeitverlauf vergleichen</h3>
        <KpiMultiSelect options={availableOptions} selected={selected} onToggle={toggle} />
      </div>
      {groups.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Wähle oben mindestens eine Kennzahl aus, um ihren Zeitverlauf zu vergleichen.
        </p>
      ) : (
        groups.map((group) => <GroupChart key={group.map((o) => o.key).join("+")} options={group} series={series} />)
      )}
    </div>
  );
}
